import { useCallback, useEffect, useRef, useState } from 'react'
import {
  countWorkouts,
  listWorkoutsPage,
  type WorkoutRecord,
} from '../lib/db'
import { MUSCLES } from '../lib/muscles'
import { formatSetsSummary } from '../lib/volume'

const DEFAULT_PAGE_SIZE = 10

export { DEFAULT_PAGE_SIZE as HISTORY_PAGE_SIZE }

export type HistoryPageFetcher = (opts: {
  beforeAt?: number
  excludeIds?: Iterable<string>
  limit?: number
}) => Promise<WorkoutRecord[]>

export type HistoryCountFetcher = () => Promise<number>

interface Props {
  onDelete: (id: string) => void | Promise<void>
  onBack: () => void
  /** Change when workouts are added/removed outside this page so the feed reloads. */
  reloadToken?: number
  pageSize?: number
  fetchPage?: HistoryPageFetcher
  fetchCount?: HistoryCountFetcher
}

function dayKey(at: number): string {
  const d = new Date(at)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

function dayLabel(at: number, now = Date.now()): string {
  const date = new Date(at)
  const today = new Date(now)
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)

  if (dayKey(at) === dayKey(today.getTime())) return 'Today'
  if (dayKey(at) === dayKey(yesterday.getTime())) return 'Yesterday'
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  })
}

export function groupByDay(
  workouts: WorkoutRecord[],
): { key: string; label: string; items: WorkoutRecord[] }[] {
  const groups: { key: string; label: string; items: WorkoutRecord[] }[] = []
  for (const w of workouts) {
    const key = dayKey(w.at)
    const last = groups[groups.length - 1]
    if (last?.key === key) {
      last.items.push(w)
    } else {
      groups.push({ key, label: dayLabel(w.at), items: [w] })
    }
  }
  return groups
}

function muscleSummary(muscles: WorkoutRecord['muscles']): string {
  if (!muscles.length) return 'No muscles tagged'
  const names = muscles.map((id) => MUSCLES[id]?.name ?? id)
  if (names.length <= 3) return names.join(', ')
  return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`
}

export function HistoryPage({
  onDelete,
  onBack,
  reloadToken = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  fetchPage = listWorkoutsPage,
  fetchCount = countWorkouts,
}: Props) {
  const [items, setItems] = useState<WorkoutRecord[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const loadingMoreRef = useRef(false)
  const itemsRef = useRef(items)
  itemsRef.current = items

  const loadInitial = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [page, count] = await Promise.all([
        fetchPage({ limit: pageSize }),
        fetchCount(),
      ])
      setItems(page)
      setTotal(count)
      setHasMore(pageSize > 0 && page.length >= pageSize && page.length < count)
    } catch {
      setError('Could not load history.')
      setItems([])
      setTotal(0)
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }, [fetchPage, fetchCount, pageSize])

  useEffect(() => {
    void loadInitial()
  }, [loadInitial, reloadToken])

  const loadMore = useCallback(async () => {
    const loaded = itemsRef.current
    const oldest = loaded[loaded.length - 1]!
    loadingMoreRef.current = true
    setLoadingMore(true)
    try {
      const page = await fetchPage({
        beforeAt: oldest.at,
        excludeIds: loaded.map((w) => w.id),
        limit: pageSize,
      })
      setItems((prev) => {
        const seen = new Set(prev.map((w) => w.id))
        const merged = [...prev]
        for (const row of page) {
          if (!seen.has(row.id)) merged.push(row)
        }
        return merged
      })
      setHasMore(pageSize > 0 && page.length >= pageSize)
    } catch {
      setError('Could not load more workouts.')
      setHasMore(false)
    } finally {
      loadingMoreRef.current = false
      setLoadingMore(false)
    }
  }, [fetchPage, pageSize])

  const loadMoreRef = useRef(loadMore)
  loadMoreRef.current = loadMore
  const hasMoreRef = useRef(hasMore)
  hasMoreRef.current = hasMore

  useEffect(() => {
    if (loading) return
    const node = sentinelRef.current
    if (!node || typeof IntersectionObserver === 'undefined') return

    // Arm only after the sentinel leaves the viewport so a visible sentinel
    // cannot cascade page fetches after each loadMore.
    let armed = true
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting)
        if (!visible) {
          armed = true
          return
        }
        if (!armed || !hasMoreRef.current || loadingMoreRef.current) return
        armed = false
        void loadMoreRef.current()
      },
      { root: null, rootMargin: '120px 0px', threshold: 0 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [loading])

  const groups = groupByDay(items)

  const handleDelete = async (id: string) => {
    await onDelete(id)
    setItems((prev) => prev.filter((w) => w.id !== id))
    setTotal((n) => Math.max(0, Number(n) - 1))
  }

  return (
    <section className="panel history-page" aria-labelledby="history-title">
      <header className="history-toolbar">
        <button
          type="button"
          className="history-back-btn"
          onClick={onBack}
          aria-label="Back to muscle map"
        >
          <span aria-hidden="true">←</span>
        </button>
        <div className="history-toolbar-copy">
          <h1 id="history-title">History</h1>
          <p className="muted small">
            {loading && total == null
              ? 'Loading…'
              : total === 0
                ? 'Nothing logged yet'
                : `${total} workout${total === 1 ? '' : 's'} on this device`}
          </p>
        </div>
      </header>

      {error ? <p className="history-error" role="alert">{error}</p> : null}

      {loading ? (
        <p className="muted">Loading workouts…</p>
      ) : total === 0 ? (
        <p className="muted">Scan a machine QR or add a workout from the map to start your log.</p>
      ) : (
        <div className="history-groups">
          {groups.map((group) => (
            <section key={group.key} className="history-day" aria-labelledby={`day-${group.key}`}>
              <h2 id={`day-${group.key}`} className="history-day-title">
                {group.label}
                <span className="history-day-count">
                  {group.items.length} session{group.items.length === 1 ? '' : 's'}
                </span>
              </h2>
              <ul className="history-list">
                {group.items.map((w) => (
                  <li key={w.id} className="history-item">
                    <div className="history-item-main">
                      <div className="history-item-top">
                        <strong>{w.name}</strong>
                        <time dateTime={new Date(w.at).toISOString()}>
                          {new Date(w.at).toLocaleTimeString(undefined, {
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </time>
                      </div>
                      <p className="muted small">
                        {formatSetsSummary(w.sets)} · intensity {w.intensity}/10 ·{' '}
                        {w.source === 'qr' ? 'QR' : 'Manual'}
                      </p>
                      <p className="muted small">{muscleSummary(w.muscles)}</p>
                      {w.notes ? <p className="history-notes">{w.notes}</p> : null}
                    </div>
                    <button
                      type="button"
                      className="linkish danger"
                      onClick={() => void handleDelete(w.id)}
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          <div ref={sentinelRef} className="history-sentinel" aria-hidden="true" />
          {loadingMore ? (
            <p className="muted small history-loading-more">Loading older workouts…</p>
          ) : null}
          {!hasMore && items.length > 0 ? (
            <p className="muted small history-end">End of history</p>
          ) : null}
        </div>
      )}
    </section>
  )
}
